import React, { useContext } from "react";
import dynamic from "next/dynamic";
import classnames from "classnames";

//Style
import Styles from "./MarketplaceLoadCard.module.scss";

//Constant
import {
    EXPECTED_AMOUNT_TYPE,
    getLorryDetailsByType,
    LOAD_STATUS,
    LORRY_BODY_TYPE_MAP
} from "@vahak/core/dist/constants";

//Components
import IfCanAccess from "../../admin/IfCanAccess/IfCanAccess";
import SourceDestination from "../../posting-form/common/src-dest/SourceDestination";
import LineDivider from "@vahak/core-ui/dist/components/LineDivider";
import MarketplaceLoadLorryQuickInfo from "../marketplace-load-lorry-quick-info/MarketplaceLoadLorryQuickInfo";
import Flex, { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";
import LoadingTimeBadge from "../loading-time-badge/LoadingTimeBadge";
import UserProfileInfo from "../user-profile-info/UserProfileInfo";
import ExpectedPriceAndBidNow from "../expected-price-and-bid-now/ExpectedPriceAndBidNow";
import Button from "@vahak/core-ui/dist/components/Button";
import Typography from "@vahak/core-ui/dist/components/Typography";
import Dropdown from "@vahak/core/dist/components/dropdown/Dropdown";

//Hooks
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks/useToggleModalWithLocalState";
import { AppContext } from "@vahak/core/dist/app-context";

//Services
import { useDeactivateLoad } from "@vahak/core/dist/_services/deactivateLoadLorry.service";

//Types && constants
import { Colors, DotSymbol } from "@vahak/core-ui/dist/constants";
import { ADMIN_OPERATION } from "../../../constants/adminOperation";
import { LoadMarketCard } from "../../../services/load/useGetLoadMarketV2/type";

//Assets
import DotsMoreIcon from "@vahak/core/dist/standard-icons/options.svg";
import VahakAssured from "@vahak/core/dist/icons/vahakAssured.svg";
import VahakAssuredInstantBooking from "@vahak/core/dist/icons/ribbon-assured-instant-booking.svg";
import VerifiedTag from "@vahak/core/dist/icons/verifiedTag.svg";
import ShareIcon from "@vahak/core/dist/standard-icons/shareIcon.svg";
import CallCircle from "@vahak/core/dist/icons/call-circle.svg";
import LocationGrey from "@vahak/core/dist/icons/location-grey.svg";

import { AgentBadgeWithLabel } from "../../admin/badge/AgentBadge";
import RelevancyTags from "../relevancy-tags/RelevancyTags";
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import { useGetContactDetails } from "../../../hooks/useGetContactDetails";
import { C2C_REFERENCE_TYPE } from "../../../constants/c2c-reference-type";
import { GenericObject } from "@vahak/core-ui/dist/types";
import { formatDate, stringOperations, toValidDate } from "@vahak/core-ui/dist/methods";
import { useEventTrackerService } from "@vahak/core/dist/_services";
import Skeleton from "@vahak/core/dist/components/skeleton/Skeleton";
import Badge from "@vahak/core-ui/dist/components/Badge";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { checkTsoTagged } from "../../../methods/admin";
import LoadStopsPopup from "../../listing-v2/load-card/stops-view/LoadStopsPopup";
import Chip from "@vahak/core-ui/dist/components/Chip";
import DisableLoadWithRemarks from "../../admin/load/load-disable/DisableLoadWithRemarks";
import OdcDetails from "./OdcDetails";
import { LoadTicketTypeToAgentTypeNameMap } from "../../admin/ticket-system-helper/constants";
import { LatLng, openGoogleMapsRoute } from "../../../methods/maps";

interface MarketplaceLoadCardProps {
    id?: string;
    data: LoadMarketCard;
    onBidNowClick?: () => void;
    handleClickProfile?: () => void;
    evenSourceScreen?: string;
    mobileResponsive?: boolean;
    onRefetch?: Function;
    flags?: ("expired_time" | "gmap_route" | "assigned-agents")[];
    destRouteLanLng?: LatLng;
    c2cReferenceType?: C2C_REFERENCE_TYPE;
    showCompanyInfo?: boolean;
}

const MarketplaceLoadCard = ({
    id,
    data,
    onBidNowClick,
    handleClickProfile,
    evenSourceScreen,
    mobileResponsive = true,
    onRefetch,
    flags,
    destRouteLanLng,
    c2cReferenceType = C2C_REFERENCE_TYPE.LOAD_MARKETPLACE,
    showCompanyInfo = false
}: MarketplaceLoadCardProps) => {
    const { setShowShareModalPopup, companyId, isCsDashboardUser, route } = useContext(AppContext);

    const toggleShareModal = () => {
        setShowShareModalPopup?.({
            isVisible: true,
            mktType: "load",
            data: {
                id: data.load_detail.id,
                source: data.load_detail.source_city_name,
                destination: data.load_detail.destination?.split("(")[0],
                quantity: data.load_detail.quantity,
                lorry_type_id: getLorryDetailsByType(data?.load_detail?.lorry_type_id)?.id,
                lorry_type_text: data.load_detail.lorry_type,
                transporter_name: data.load_detail.destination_city_name,
                oppositeCompanyId: companyId,
                loadCardData: data
            }
        });
        // if (isTso) {
        //     trackClick("share_clicked");
        // }
    };

    const {
        isModalOpen: isAdminOptionMenuOpen,
        closeModal: closeAdminOptionMenu,
        toggleModal: toggleAdminOptionMenu
    } = useToggleModalWithLocalState(false);
    const {
        isModalOpen: isLoadDeletionModalOpen,
        openModal: openLoadDeletionModal,
        closeModal: closeLoadDeletionModal
    } = useToggleModalWithLocalState(false);

    const { connectC2C } = useGetContactDetails(data?.company_detail?.id.toString());

    const _onClickGetContactTso = () => {
        connectC2C({
            callerId: Number(companyId),
            calleeId: Number(data.company_detail.id.toString()),
            referenceId: Number(data.load_detail.id),
            referenceType: c2cReferenceType
        });
        handleTracking?.("call_initiated");
    };

    const { sendGAandMoEngageEvent } = useEventTrackerService();

    const handleTracking = (name: string, additionalData: GenericObject = {}) => {
        const eventData = {
            source_screen: evenSourceScreen || "tso_load_market",
            tso_tagged: !!data?.agent_detail?.agent_name,
            ...(!!data?.agent_detail?.agent_name && {
                tagged_tso_id: data?.agent_detail?.user_id
            }),
            ...additionalData,
            load_id: data.load_detail.id,
            is_load_verified: data.load_detail.is_verified,
            from_location: data.load_detail.source,
            to_location: data.load_detail.destination,
            material: data.load_detail.material_type,
            quantity: data.load_detail.quantity,
            required_vehicle_type: data.load_detail.lorry_type_id,
            expected_price: data.load_detail.amount,
            expected_price_type: stringOperations(EXPECTED_AMOUNT_TYPE[data.load_detail.payment_type!] ?? "", [
                "toLowerCase",
                "replaceSpacesWithUnderscore"
            ])
        };

        sendGAandMoEngageEvent({
            name,
            data: eventData
        });
    };

    const isActiveLoad = data?.load_detail?.status === LOAD_STATUS.ACTIVE;
    const isExpiredLoad = data?.load_detail?.status === LOAD_STATUS.EXPIRED;
    return (
        <div className={classnames(Styles.main, mobileResponsive && Styles.mobileResponsive)}>
            <div className={Styles.headerContent}>
                <Flex justifyContent="space-between" alignItems="center">
                    {/* {data.load_detail.is_tap_n_go ? (
                        <>
                            {data.load_detail.meta_data?.is_verification_amount_paid ? (
                                <VahakAssuredInstantBooking />
                            ) : (
                                <div style={{ marginLeft: "8px" }}>
                                    <VahakAssured />
                                </div>
                            )}
                        </>
                    ) :  undefined} */}
                    {!!data.load_detail.is_verified && <VerifiedTag />}
                    {isActiveLoad && data.load_detail.meta_data?.limited_bids && data.load_detail.is_spot_load ? (
                        <div style={{ marginLeft: "15px" }}>
                            <Typography size="xs" color={COLORS.ORANGE} weight="semibold">
                                {(data.load_detail.meta_data?.limited_bids ?? 0) -
                                    (data.load_detail.total_bids_received ?? 0) +
                                    "/" +
                                    data.load_detail.meta_data?.limited_bids}{" "}
                                slots remaining
                            </Typography>
                        </div>
                    ) : (
                        <></>
                    )}
                    {isActiveLoad && (
                        <Flex alignItems="center" className={Styles.actionBtnWrapper}>
                            <IfCanAccess action={ADMIN_OPERATION.userLoadLorryAction}>
                                <>
                                    <Dropdown
                                        isOpen={isAdminOptionMenuOpen}
                                        DropdownToggleBtn={
                                            <div className={Styles.langIconWrapper} style={{ cursor: "pointer" }}>
                                                <DotsMoreIcon />
                                            </div>
                                        }
                                        handleToggle={toggleAdminOptionMenu}
                                        dropdownClassName={Styles.adminOptionDropdown}
                                        dropdownToggleClassName={Styles.adminOptionDropdownToggle}
                                    >
                                        <div style={{ marginLeft: "10px" }}>
                                            <Button
                                                fillType="text"
                                                size="s"
                                                onClick={() => {
                                                    closeAdminOptionMenu();
                                                    openLoadDeletionModal();
                                                }}
                                            >
                                                Remove Load
                                            </Button>
                                        </div>
                                    </Dropdown>

                                    {isLoadDeletionModalOpen && (
                                        <DisableLoadWithRemarks
                                            isOpen={isLoadDeletionModalOpen}
                                            onToggle={closeLoadDeletionModal}
                                            loadId={Number(data.load_detail.id)}
                                            onRefetch={onRefetch}
                                        />
                                    )}
                                </>
                            </IfCanAccess>

                            <div className={Styles.shareBtn}>
                                <Button fillType="text" onClick={toggleShareModal} id={`${id}--share`}>
                                    <Typography color={Colors.GREY_400} size="xs">
                                        <ShareIcon /> Share
                                    </Typography>
                                </Button>
                            </div>
                        </Flex>
                    )}
                </Flex>
            </div>
            <div className={Styles.bodyContent}>
                {/* {isCsDashboardUser && (
                    <>
                        <div style={{ margin: "4px 0" }}>
                            <Chip outlined size="auto" radius={8} color="grey" filled weight="semibold">
                                #LD-{data.load_detail.id}
                            </Chip>
                        </div>
                    </>
                )} */}
                <SourceDestination
                    id={id}
                    source={data.load_detail.source_formatted_address}
                    sourceCity={
                        (data.load_detail.source_locality_name ? data.load_detail.source_locality_name + ", " : "") +
                        data.load_detail.source.split("(")[0]
                    }
                    sourceState={data.load_detail.source.split("(")[1].replace(")", "")}
                    destination={data.load_detail.destination_formatted_address}
                    destCity={
                        (data.load_detail.destination_locality_name
                            ? data.load_detail.destination_locality_name + ", "
                            : "") + data.load_detail.destination.split("(")[0]
                    }
                    destState={data.load_detail.destination.split("(")[1].replace(")", "")}
                    distanceInKm={Math.ceil(data.load_detail.distance / 1000)}
                    ellipses
                    {...(flags?.includes("gmap_route") && {
                        route: [
                            [data?.load_detail?.source_latitude!, data?.load_detail?.source_longitude!],
                            [data?.load_detail?.destination_latitude!, data?.load_detail?.destination_longitude!]
                        ]
                    })}
                />
                {!!data?.load_detail?.stops?.length && (
                    <Flex justifyContent="space-between" margin={"9px 0"}>
                        <Chip
                            size="auto"
                            filled
                            fillColor={COLORS.GREY_500}
                            faceColor={COLORS.WHITE}
                            leftNode={
                                <IconWrapper svgFillColor={COLORS.WHITE}>
                                    <LocationGrey />
                                </IconWrapper>
                            }
                        >
                            MULTI STOP LOAD
                        </Chip>
                        <LoadStopsPopup loadId={data?.load_detail?.id} actionText="Show locations" />
                    </Flex>
                )}

                {isCsDashboardUser && flags?.includes("gmap_route") && destRouteLanLng && (
                    <Flex justifyContent="center" margin={"4px 0"}>
                        <Typography
                            color={COLORS.RED}
                            size="xs"
                            weight="semibold"
                            onClick={() => {
                                const { source_latitude, source_longitude } = data?.load_detail || {};
                                openGoogleMapsRoute([[source_latitude!, source_longitude!], destRouteLanLng!]);
                            }}
                            underline
                        >
                            View Distance from Lorry Current Location
                        </Typography>
                    </Flex>
                )}

                <LineDivider styleType="solid" linearGradient={true} />

                <Flex gap={12} flexDirection="column" margin={"9px 0"}>
                    <MarketplaceLoadLorryQuickInfo
                        id={id}
                        lorryType={data.load_detail.lorry_type_id}
                        type="lorry"
                        tyres={data.load_detail.total_tyres}
                        fontWeight="medium"
                        bodyType={LORRY_BODY_TYPE_MAP[data.load_detail.body_type]}
                        lorrySize={data.load_detail.size}
                        lorrySizeText={data.load_detail.size_text}
                    />
                    <MarketplaceLoadLorryQuickInfo
                        id={id}
                        type="load"
                        fontWeight="medium"
                        material={data.load_detail.material_type}
                        quantity={data.load_detail.quantity}
                        lorryType={data.load_detail.lorry_type_id}
                    />
                    {data.load_detail.is_odc && (
                        <OdcDetails
                            isOdc={data.load_detail.is_odc}
                            height={data.load_detail.height}
                            breadth={data.load_detail.breadth}
                            length={data.load_detail.length}
                        />
                    )}

                    <LoadingTimeBadge
                        id={id}
                        time={
                            data.load_detail.loading_timestamp
                                ? new Date(data.load_detail.loading_timestamp * 1000)
                                : undefined
                        }
                    />
                    {isCsDashboardUser && isExpiredLoad && flags?.includes("expired_time") && (
                        <VerticalFlex>
                            {!!data.load_detail.expiration_hour && (
                                <Typography size="sm" color={COLORS.RED} weight="bold">
                                    {formatDate(
                                        toValidDate(data.load_detail.expires_at_timestamp! * 1000, ""),
                                        "Expired at hh:mm a (DD/MM/YY)"
                                    )}
                                </Typography>
                            )}
                        </VerticalFlex>
                    )}
                </Flex>
                {showCompanyInfo && data?.company_detail?.id && (
                    <>
                        <LineDivider styleType="solid" linearGradient={true} />

                        <div style={{ margin: "9px 0px 13px 0" }}>
                            <UserProfileInfo
                                id={id}
                                companyId={data.company_detail.id}
                                companyLogo={data.company_detail.logo}
                                name={data.company_detail.name}
                                isBankVerified={data.company_detail.is_bank_verified}
                                isVerified={data.company_detail.is_company_verified}
                                isAadhaarVerified={data.company_detail.is_aadhaar_verified}
                                isGstVerified={data.company_detail.is_gst_verified}
                                isPanVerified={data.company_detail.is_company_verified}
                                isMember={data.company_detail.is_member}
                                rating={data.company_detail.ratings}
                                serviceType={data.company_detail.service_type}
                                handleClickProfile={handleClickProfile}
                                phoneNumber={data?.company_detail?.phone_number}
                                agentTagged={!!data.agent_detail}
                                revealCompanyInfo={isCsDashboardUser}
                                onReveal={() =>
                                    handleTracking("reveal_info", {
                                        reveal_target: "load_owner",
                                        load_id: data.load_detail.id,
                                        source_screen: route
                                    })
                                }
                            />
                            <IfCanAccess action={""} allowCsUser>
                                <LineDivider styleType="solid" linearGradient={true} margin="10px 0" />
                                <Flex justifyContent="flex-start" alignItems="flex-start" gap={12} flexWrap="wrap">
                                    {flags?.includes("assigned-agents") &&
                                        data?.assigned_to_details?.map((agent) => {
                                            const label = agent?.ticket_type
                                                ? LoadTicketTypeToAgentTypeNameMap[agent?.ticket_type]
                                                : "";
                                            return <AgentBadgeWithLabel type="cs" data={agent} label={label} />;
                                        })}

                                    <Flex justifyContent="flex-start" gap={4} alignItems="center">
                                        <AgentBadgeWithLabel type="tso" data={data.agent_detail} label="TSO" />
                                        {!!checkTsoTagged(data.agent_detail) && (
                                            <IfCanAccess
                                                action={ADMIN_OPERATION.tagAgent}
                                                alsoCheck={!!data.company_detail.id}
                                            >
                                                <span className={Styles.callBtn} onClick={_onClickGetContactTso}>
                                                    <IconWrapper width={24} height={24}>
                                                        <CallCircle />
                                                    </IconWrapper>
                                                </span>
                                            </IfCanAccess>
                                        )}
                                    </Flex>
                                </Flex>
                            </IfCanAccess>
                        </div>
                    </>
                )}
            </div>

            <div
                className={classnames(
                    Styles.footerContent
                    // companyId && !data.relevancy_tags.tags && Styles.singleChild
                )}
            >
                <ExpectedPriceAndBidNow
                    id={id}
                    onBidNowClick={onBidNowClick}
                    advanceAmount={data.load_detail.advance_payment_percentage}
                    expectedPrice={data.load_detail.amount}
                    paymentType={data.load_detail.payment_type}
                    pricePerTonPerKm={Number(data.load_detail.price_per_tonne_per_km.toFixed(3))}
                    additionalCharge={data?.load_detail?.meta_data?.loading_charges_detail?.charges}
                    isBestPrice={!!data.load_detail.meta_data?.is_best_price}
                />
                {/* <PeopleViewingCount count={3} id={id} /> */}
                {/* {!!data?.relevancy_tags?.tags?.length && <RelevancyTags tags={data?.relevancy_tags?.tags} />} */}
            </div>
        </div>
    );
};

export default MarketplaceLoadCard;

export const MarketplaceLoadCardShimmer = ({
    mobileResponsive = true
}: Pick<MarketplaceLoadCardProps, "mobileResponsive">) => {
    return (
        <div
            className={classnames(Styles.main, mobileResponsive && Styles.mobileResponsive)}
            style={{ display: "flex", flexDirection: "column", padding: 20, gap: 8 }}
        >
            <Skeleton type="button" width={50} height={50} />
            <br />
            <Skeleton type="button" />
            <Skeleton type="button" />
            <Skeleton type="button" />
            <br />

            <Skeleton type="button" height={50} />
        </div>
    );
};
